// Homepage hero rotator (sections/stally-rd-hero.liquid): from page load, a new slide every 5 seconds, held while
// the mouse or keyboard focus is in the hero, the hero is off-screen, the tab is hidden or a theme-editor block is
// selected; the arrows and a swipe step through it and restart the wait. Reduced motion never rotates on its own.
// The stop / start button follows the W3C APG auto-rotating carousel example: Stop holds it until Start, and Start
// rotates even with the mouse or keyboard focus in the hero (focus on the button itself never holds it).
const INTERVAL = 5000;
const SWIPE = 40;
const started = new WeakSet();

function start(hero) {
  if (started.has(hero)) return;
  started.add(hero);
  const slides = [...hero.querySelectorAll('.hero-slide')];
  const stack = hero.querySelector('.hero-slides');
  const arrows = [...hero.querySelectorAll('.hero-arrow')];
  const toggle = hero.querySelector('.hero-play');
  const holds = new Set();
  const autoplay = !matchMedia('(prefers-reduced-motion: reduce)').matches;
  let index = 0;
  let timer = 0;
  let loaded = false;
  let played = false;

  const show = next => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      if (i === index) slide.hidden = false;
      slide.toggleAttribute('inert', i !== index);
    });
  };
  const schedule = () => {
    clearTimeout(timer);
    if (!hero.isConnected) return;
    const rotating = loaded && autoplay && !holds.size && !document.hidden;
    // W3C APG carousel: announce slide changes only while nothing changes them on its own
    stack.setAttribute('aria-live', rotating ? 'off' : 'polite');
    if (rotating) timer = setTimeout(() => { show(index + 1); schedule(); }, INTERVAL);
  };
  const step = by => { show(index + by); schedule(); };
  const hold = (reason, on) => {
    // after Start, the mouse and keyboard focus no longer hold the rotation
    if (on && played && (reason === 'pointer' || reason === 'focus')) return;
    if (holds.has(reason) === on) return;
    if (on) holds.add(reason); else holds.delete(reason);
    schedule();
  };
  const stop = on => {
    played = !on;
    if (played) { holds.delete('pointer'); holds.delete('focus'); }
    toggle.toggleAttribute('data-stopped', on);
    toggle.setAttribute('aria-label', on ? 'Start automatic slide show' : 'Stop automatic slide show');
    hold('stopped', on);
    schedule();
  };

  toggle.addEventListener('click', () => stop(!holds.has('stopped')));
  hero.querySelector('.hero-prev').addEventListener('click', () => step(-1));
  hero.querySelector('.hero-next').addEventListener('click', () => step(1));
  hero.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') hold('pointer', true); });
  hero.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') hold('pointer', false); });
  // keyboard focus only: a clicked or tapped arrow keeps focus, and the rotation should carry on once the mouse
  // leaves, so a pointer press also ends a keyboard hold. Focus on a slide's own link holds it even after Start
  // ('slide' is not a hold Start overrides): rotating would make that slide inert and drop the focus to the page.
  hero.addEventListener('focusin', event => {
    const keyboard = event.target.matches(':focus-visible') && event.target !== toggle;
    hold('focus', keyboard);
    hold('slide', keyboard && Boolean(event.target.closest('.hero-slide')));
  });
  hero.addEventListener('focusout', event => { if (!hero.contains(event.relatedTarget)) { hold('focus', false); hold('slide', false); } });
  document.addEventListener('visibilitychange', schedule);
  new IntersectionObserver(entries => hold('offscreen', !entries.at(-1).isIntersecting)).observe(hero);
  // touch: a mostly sideways swipe steps (the hero's touch-action leaves vertical scrolling and zoom to the browser)
  let touch = null;
  hero.addEventListener('pointerdown', event => { hold('focus', false); hold('slide', false); touch = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null; });
  hero.addEventListener('pointerup', event => {
    if (!touch) return;
    const dx = event.clientX - touch.x, dy = event.clientY - touch.y;
    touch = null;
    if (Math.abs(dx) >= SWIPE && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
  });
  hero.addEventListener('pointercancel', () => { touch = null; });
  // theme editor: selecting a slide's block shows it and holds there
  hero.addEventListener('shopify:block:select', event => {
    const i = slides.indexOf(event.target.closest('.hero-slide'));
    if (i >= 0) show(i);
    hold('editor', true);
  });
  hero.addEventListener('shopify:block:deselect', () => hold('editor', false));

  // The later slides stay display:none until the page has loaded (or a visitor steps to one), so their photos don't
  // compete with the first one, and the rotation starts then: on a slow phone it would otherwise move on before the
  // first photo arrived. Window load waits for third-party scripts too (about 13 s on a throttled phone with the
  // Instafeed requests blocked, Oct 16).
  const reveal = () => { loaded = true; slides.forEach(slide => { slide.hidden = false; }); schedule(); };
  if (document.readyState === 'complete') reveal(); else addEventListener('load', reveal, { once: true });
  arrows.forEach(arrow => { arrow.hidden = false; });
  // with reduced motion nothing rotates, so there is nothing to stop
  toggle.hidden = !autoplay;
  schedule();
}

const startAll = root => root.querySelectorAll('.hero-rotator .hero-prev').forEach(prev => start(prev.closest('.hero-rotator')));
startAll(document);
document.addEventListener('shopify:section:load', event => startAll(event.target));
